/**
 * Browser Logger Utility
 *
 * Simple structured logging wrapper for browser environments using loglevel.
 * Can be extended to send logs to external services in production.
 */

import log from 'loglevel';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

class BrowserLogger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    const logLevel =
      import.meta.env.VITE_LOG_LEVEL || (this.isDevelopment ? 'debug' : 'info');
    log.setLevel(logLevel as log.LogLevelDesc);

    // Expose log to window for runtime control
    if (typeof window !== 'undefined') {
      (window as any).log = log;
    }
  }

  private log(level: LogLevel, message: string, context?: LogContext) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      message,
      ...context,
    };

    // Get the appropriate log function in a type-safe way
    const logFnMap: Record<LogLevel, (...args: unknown[]) => void> = {
      debug: log.debug.bind(log),
      info: log.info.bind(log),
      warn: log.warn.bind(log),
      error: log.error.bind(log),
    };
    const logFn = logFnMap[level];

    // In development, use loglevel with context
    if (this.isDevelopment) {
      if (context) {
        logFn(`[${level.toUpperCase()}] ${message}`, context);
      } else {
        logFn(`[${level.toUpperCase()}] ${message}`);
      }
    } else {
      // In production, log structured JSON
      // Could be extended to send to external logging service
      logFn(JSON.stringify(logEntry));
    }
  }

  debug(message: string, context?: LogContext) {
    this.log('debug', message, context);
  }

  info(message: string, context?: LogContext) {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log('warn', message, context);
  }

  error(message: string, context?: LogContext) {
    this.log('error', message, context);
  }
}

export const logger = new BrowserLogger();
